import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Progressbar } from 'framework7-react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { create, groupBy, keyBy, orderBy } from 'lodash';
import Markdown from 'markdown-to-jsx';
import mixpanel from 'mixpanel-browser';

import API from '@api/API';

import {
  Entertainer,
  EntertainerFilter,
  useCurrentEntertainerQuery,
  useEntertainerByIdQuery,
  useFilteredEntertainersQuery,
  useHydratedEntertainersQuery,
} from '@queries/EntertainerQueries';
import {
  EventReview,
  SeriesEventBonusTipVotes,
  useEventCompetitionDataQuery,
  useEventQuery,
  useEventReviewsQuery,
} from '@queries/EventQueries';
import { useUserConnectionsQuery } from '@queries/UserConnectionsQueries';
import { useCurrentUser } from '@queries/UserQueries';

import {
  useSubmitBonusTipVoteMutation,
  useToggleEventCompetitionLikeMutation,
  useUpdateEventMutation,
} from '@mutations/EventMutations';

import { useIsPastEvent } from '@hooks/EventHooks/useIsPastEvent';
import { useAnalytics } from '@hooks/useAnalytics';
import { useDailyLocalStorageReminderIsVisible } from '@hooks/useDailyLocalStorageReminderIsVisible';
import { useReferredBy } from '@hooks/useReferredBy';
import { useShareLink } from '@hooks/useShareLink';

import { useNavigate, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { getEntertainerSeoLink } from '@utilities/entertainerUtilities/getEntertainerSeoLink';
import { formatCentsAmountToDollars } from '@utilities/formatCentsAmountToDollars';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import { EventHeader } from '@components/cards/EventCard';
import { AdvancedPieChart } from '@components/charts/AdvancedPieChart';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { InteractiveHeartIcon } from '@components/icons/InteractiveHeartIcon';
import Card from '@components/layout/Card/Card';
import { DangerBanner } from '@components/layout/DangerBanner/DangerBanner';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen';
import { SeoLink } from '@components/layout/SeoLink/SeoLink';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { UserAvatar } from '@components/layout/UserAvatar/UserAvatar';

import { useJoinLineup } from '@screens/CommunityScreen/components/ShowsTabContent';
import { CreateEditEventPopup } from '@screens/CreateEditEventScreen/CreateEditEventForm';
import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { SupportEntertainerCard } from '@screens/entertainer/DragKingCommunityScreen/DragKingThingShoutoutsScreen';
import { EntertainerRewiewsPopup } from '@screens/entertainer/EntertainerDetailsScreen/EntertainerReviews';
import calendarIconUrl from '@screens/entertainer/EntertainerDetailsScreen/assets/calendar.svg';
import dollarsSvgUrl from '@screens/series/SeriesScreen/dollars.svg';

import { EventRsvpsSection } from '../EventRsvpScreen/components/EventRsvpsSection';
import { useEventRsvpActions } from '../EventRsvpScreen/hooks/useEventRsvpActions';
import { CastReferralPopup } from './CastReferralPopup';
import { TipEntertainerButton, UnconfirmedEntertainerButton } from './EventScreen';
import fireAnimation from './assets/fire.json';

const useSubmitEventCommentMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      eventId,
      distinctId,
      comment,
      referredBy,
      isCompetitionReview,
      parentReviewId,
    }: {
      eventId: string;
      distinctId: string;
      comment: string;
      referredBy?: string;
      isCompetitionReview?: boolean;
      parentReviewId?: string;
    }) =>
      API.post<
        { eventReview: EventReview },
        {
          distinctId: string;
          comment: string;
          referredBy?: string;
          isCompetitionReview?: boolean;
          parentReviewId?: string;
        }
      >(`/v1/events/${eventId}/reviews`, {
        distinctId,
        comment,
        referredBy,
        isCompetitionReview,
        parentReviewId,
      }),
    onSuccess: () => {},
  });

  return {
    isSubmittingEventComment: isPending,
    submitEventCommentAsync: mutateAsync,
  };
};

const useEntertainerFollowUpAuthDialog = () => {
  const { openConfirmDialog } = useDialog();
  const { createAuthGateHandler } = useAuthGate();
  const { entertainer } = useCurrentEntertainerQuery();
  const { track } = useAnalytics();

  const ENABLE_ENTERTAINER_FOLLOWUP = false;
  const openEntertainerFollowUpAuthDialog = () => {
    if (!entertainer?._id && ENABLE_ENTERTAINER_FOLLOWUP) {
      openConfirmDialog({
        title: 'Are you a drag performer?',
        text: 'Support means more when it comes from a fellow performer!',
        buttonCancel: "No I'm not",
        buttonOk: 'Yes, I am!',
        onConfirm: () => {
          track('Confirm is entertainer');
          createAuthGateHandler(() => {}, {
            entertainerCallback: (entertainerProperties) => {
              track('Entered entertainer properties', entertainerProperties);
            },
          });
        },
      });
    }
  };

  return { openEntertainerFollowUpAuthDialog };
};

const useForceAuth = () => {
  const { track } = useAnalytics();
  const { createAuthGateHandler } = useAuthGate();

  const openAuthGate: typeof createAuthGateHandler = (authCallback) => {
    track('Force auth', {});
    createAuthGateHandler(authCallback, {
      authMessage:
        'A lot of money is on the line! To prevent bots, we require phone number verification, but no worries, it stays confidential and is never shared.',
    });
  };

  return { openAuthGate };
};

const FormContent: FC<{
  replyReview?: EventReview;
  hasFeatureBeenPosted?: boolean;
  lastShowFeaturePostDate?: string;
  eventId: string;
  onSubmit: (submittedComment: EventReview) => void;
  forceAuth: boolean;
}> = ({
  hasFeatureBeenPosted,
  lastShowFeaturePostDate,
  eventId,
  onSubmit,
  replyReview,
  forceAuth,
}) => {
  const { track } = useAnalytics();
  const { referredBy } = useReferredBy();

  const didSubmit = useRef(false);
  const distinctId = mixpanel.get_distinct_id();
  const [comment, setComment] = useState('');
  const { isSubmittingEventComment, submitEventCommentAsync } = useSubmitEventCommentMutation();
  const [hasError, setHasError] = useState(false);
  const { openAuthGate } = useForceAuth();
  const { openEntertainerFollowUpAuthDialog } = useEntertainerFollowUpAuthDialog();

  const handleUpdateComment = (updatedComment: string) => {
    setHasError(false);
    setComment(updatedComment);
  };

  const submitButtonText = useMemo(() => {
    if (forceAuth) {
      const submitButtonVariants = [
        'Share your opinion',
        'Share your thoughts',
        'Share your critiques',
      ];

      return submitButtonVariants[Math.floor(Math.random() * submitButtonVariants.length)];
    }

    return 'Share your thoughts';
  }, [forceAuth]);

  const handleSubmitComment = async () => {
    if (!didSubmit.current) {
      if (!comment || comment.trim().length < 10) {
        setHasError(true);
        return;
      }

      track('Event Competition Review Submitted', {
        eventId,
        comment,
        submitButtonText,
        forceAuth,
        referredBy,
        parentReviewId: replyReview?._id,
      });

      didSubmit.current = true;
      try {
        const authCallback = async () => {
          const submittedCommentResponse = await submitEventCommentAsync({
            eventId,
            distinctId,
            comment,
            referredBy,
            isCompetitionReview: true,
            parentReviewId: replyReview?._id,
          });

          if (forceAuth) {
            openEntertainerFollowUpAuthDialog();
          }

          onSubmit(submittedCommentResponse?.eventReview);
        };

        if (forceAuth) {
          openAuthGate(authCallback);
        } else {
          await authCallback();
        }
      } catch (error) {}
      didSubmit.current = false;
    }
  };

  const examples = [
    `"I was so disappointed with the performance. They weren't even trying. 🙄"`,
    `"Laziest lipsync I've ever seen 🤬"`,
  ];

  return (
    <div>
      <TitleToolbar text="What do you wanna share?" size="sm" />

      {replyReview && (
        <div className="mb-2">
          <Card key={replyReview.postedAt}>
            <div className="p-3">
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                <strong>{`Shared ${dayjs(replyReview.postedAt).fromNow()}`}</strong>
              </div>
              <div className="d-grid gap-2">
                {mapStringToParagraphs(replyReview.comment, {
                  collapseTopMargin: true,
                  sharedClassName: 'my-0',
                })}
              </div>
            </div>
          </Card>
        </div>
      )}

      <Textarea
        placeholder={
          replyReview ? '💯💯💯💯💯💯💯💯💯💯💯' : 'Not my favorite going home AGAIN! 😭😭😭'
        }
        onChange={handleUpdateComment}
      />

      {hasError && (
        <div className="d-grid gap-1 text-danger mt-2">
          <div>
            This should sound like you have a strong opinion. Share a little bit more about why.
          </div>
          {examples.map((example) => (
            <div className="d-flex gap-1">
              <div>•</div>
              <div>
                <em>{example}</em>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2">
        <Button
          disabled={isSubmittingEventComment || !comment}
          onClick={handleSubmitComment}
          roundness="rounded"
        >
          {submitButtonText}
        </Button>
      </div>
    </div>
  );
};

const useUpdateEventReviewUserLikeMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      eventReviewId,
      distinctId,
      isLiked,
    }: {
      eventReviewId: string;
      distinctId: string;
      isLiked: boolean;
    }) =>
      API.post(`/v1/events-reviews/${eventReviewId}/like`, {
        distinctId,
        isLiked,
      }),
    onSuccess: () => {},
  });

  return {
    isUpdatingEventReviewUserLike: isPending,
    updateEventReviewUserLikeAsync: mutateAsync,
  };
};

const EventCommentCard: FC<{
  review: EventReview;
  reviewsGroupedByParentReviewId: Record<string, EventReview[]>;
  onReply: (review: EventReview) => void;
}> = ({ review, reviewsGroupedByParentReviewId, onReply }) => {
  const { track } = useAnalytics();
  const [isUserLiked, setIsUserLiked] = useState(review.isUserLiked);
  const [likeCount, setLikeCount] = useState(review.likeCount || 0);
  const { isUpdatingEventReviewUserLike, updateEventReviewUserLikeAsync } =
    useUpdateEventReviewUserLikeMutation();

  const handleToggleUserLike = async () => {
    if (isUpdatingEventReviewUserLike) return;

    track('Event Review Like Toggled', {
      eventReviewId: review._id,
      updatedIsLiked: !isUserLiked,
    });

    try {
      setIsUserLiked((prev) => !prev);
      await updateEventReviewUserLikeAsync({
        eventReviewId: review._id,
        distinctId: mixpanel.get_distinct_id(),
        isLiked: !isUserLiked,
      });

      if (!isUserLiked) {
        setLikeCount((prev) => prev + 1);
      } else {
        setLikeCount((prev) => (prev > 0 ? prev - 1 : 0));
      }
    } catch (error) {
      // Revert like state on error
      setIsUserLiked((prev) => prev);
    }
  };

  const handleReply = () => {
    track('Event Review Reply Clicked', {
      eventReviewId: review._id,
    });
    onReply(review);
  };

  return (
    <div>
      <Card key={review.postedAt}>
        <div className="p-3">
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            <strong>{`Shared ${dayjs(review.postedAt).fromNow()}`}</strong>
          </div>
          <div className="d-grid gap-2">
            {mapStringToParagraphs(review.comment, {
              collapseTopMargin: true,
              sharedClassName: 'my-0',
            })}
          </div>
          <div className="mt-2 d-flex gap-3">
            <div
              className="d-flex gap-2 align-items-center"
              style={{ fontSize: 14 }}
              onClick={handleToggleUserLike}
            >
              <div style={{ color: isUserLiked ? '#ff0000' : 'inherit' }}>
                <FontAwesomeIcon name="heart" faStyle={isUserLiked ? undefined : 'far'} />
              </div>
              <span>{likeCount}</span>
            </div>

            <div
              className="d-flex gap-2 align-items-center"
              style={{ fontSize: 14 }}
              onClick={handleReply}
            >
              <div>
                <FontAwesomeIcon name="comment" />
              </div>
              <span>Reply</span>
            </div>
          </div>
        </div>
      </Card>
      {reviewsGroupedByParentReviewId[review._id]?.length && (
        <div
          className="mt-2"
          style={{
            borderLeft: `4px solid rgba(255,255,255,.2)`,
            paddingLeft: 16,
          }}
        >
          {reviewsGroupedByParentReviewId[review._id]?.map((child) => (
            <EventCommentCard
              key={child._id}
              review={child}
              reviewsGroupedByParentReviewId={reviewsGroupedByParentReviewId}
              onReply={onReply}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SHARE_INSTRUCTION_LOCAL_STORAGE_KEY = 'eventReviewsScreenShareInstructionLastViewedAt';
const useShareInstructionIsVisible = (delayMs: number) => {
  const { isDailyLocalStorageReminderVisible, handleDailyLocalStorageReminderAcknowledged } =
    useDailyLocalStorageReminderIsVisible(SHARE_INSTRUCTION_LOCAL_STORAGE_KEY, delayMs, 'isoWeek');

  return {
    isShareInstructionVisible: isDailyLocalStorageReminderVisible,
    handleShareInstructionAcknowledged: handleDailyLocalStorageReminderAcknowledged,
  };
};

const InternalShareLinkButton: FC<{
  isShareInstructionVisible: boolean;
  onShareInstructionAcknowledged: () => void;
  eventId: string;
  competitionLinkUrl: string | undefined;
}> = ({
  isShareInstructionVisible,
  onShareInstructionAcknowledged,
  eventId,
  competitionLinkUrl,
}) => {
  return (
    <ShareLinkButton
      url={competitionLinkUrl ?? `https://my.gaggl.app/events/${eventId}/competition-reviews`}
    />
  );
};

const ShareSection: FC<{ eventId: string; competitionLinkUrl: string | undefined }> = ({
  eventId,
  competitionLinkUrl,
}) => {
  const { track } = useAnalytics();
  const { didCopy, shareLink } = useShareLink();
  const url = competitionLinkUrl ?? `https://my.gaggl.app/events/${eventId}/competition-reviews`;

  const handleShareClick = async () => {
    track('Click share competition link inline');
    await shareLink({
      url,
    });
  };

  return (
    <div
      className="d-flex align-items-center justify-content-between ps-2 mb-3"
      style={{
        border: '1px solid rgba(255,255,255,.2)',
        borderRadius: 8,
      }}
    >
      <div className="d-flex align-items-center gap-2" style={{ maxWidth: 'calc(100% - 75px)' }}>
        <div>
          <FontAwesomeIcon name="link" />
        </div>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {url}
          </div>
        </div>
      </div>
      <div>
        <Button
          variant="flat"
          roundness="rounded"
          size="sm"
          color="neutral"
          startIcon={
            didCopy ? <FontAwesomeIcon name="check" /> : <FontAwesomeIcon name="share-square" />
          }
          className="py-2 px-2 opacity-90"
          onClick={handleShareClick}
        >
          {didCopy ? 'Copied!' : 'Share'}
        </Button>
      </div>
    </div>
  );
};

const MILESTONES = [5, 10, 15, 25, 40, 60, 100, 150, 250, 400, 600, 1000, 1500, 2000, 2500];

const USE_PROGRESS_BAR = true;
const GREEN = '#31f207';
const YELLOW = '#fedb41';
const RED = '#e13026';

const VoteMilestoneProgress: FC<{
  eventVoteCount: number;
}> = ({ eventVoteCount }) => {
  const [initialVoteCount] = useState(eventVoteCount);

  const currentGoal = useMemo(() => {
    return MILESTONES.find((milestone) => initialVoteCount < milestone);
  }, [initialVoteCount]);

  const percentageOfGoal = eventVoteCount / currentGoal;

  const chartColor = useMemo(() => {
    const percentageInt = Math.ceil(percentageOfGoal * 100);

    if (percentageInt > 50) {
      return GREEN;
    }

    if (percentageInt > 35) {
      return YELLOW;
    }

    return RED;
  }, [percentageOfGoal]);

  return (
    <div>
      <Progressbar
        style={
          {
            '--f7-progressbar-progress-color': chartColor,
          } as any
        }
        progress={percentageOfGoal * 100}
      />
      <div className="mt-1">
        {eventVoteCount ? (
          <>
            {eventVoteCount === currentGoal ? (
              <strong>Goal reached! 🔥🔥🔥</strong>
            ) : (
              <>
                <strong>{`${eventVoteCount} supporters`}</strong> of {currentGoal} goal
              </>
            )}
          </>
        ) : (
          <strong style={{ borderLeft: `2px solid ${RED}`, paddingLeft: 4 }}>
            Be the first to support this cast!
          </strong>
        )}
      </div>
    </div>
  );
};

const HypeMilestoneProgress: FC<{
  currentUserReviewCount: number;
}> = ({ currentUserReviewCount }) => {
  const currentGoal = 4;

  const percentageOfGoal = currentUserReviewCount / currentGoal;

  const chartColor = useMemo(() => {
    const percentageInt = Math.ceil(percentageOfGoal * 100);

    if (percentageInt === 100) {
      return GREEN;
    }

    if (percentageInt >= 50) {
      return YELLOW;
    }

    return RED;
  }, [percentageOfGoal]);

  return (
    <div>
      <Progressbar
        style={
          {
            '--f7-progressbar-progress-color': chartColor,
          } as any
        }
        progress={percentageOfGoal * 100}
      />
      <div className="mt-1 text-center" style={{ fontSize: 12 }}>
        <strong
          style={currentUserReviewCount === 0 ? { borderBottom: `1px solid ${RED}` } : undefined}
        >{`${formatCentsAmountToDollars(percentageOfGoal * 500)} potential share`}</strong>{' '}
        of the $5 supporter tip pool
      </div>
    </div>
  );
};

const PercentageOfVotesChart: FC<{
  eventVoteCount: number;
  seriesBonusTipVoteCount: number;
  seriesEventBonusTipVotes: SeriesEventBonusTipVotes;
}> = ({ eventVoteCount, seriesBonusTipVoteCount, seriesEventBonusTipVotes }) => {
  const percentageOfVotes = seriesBonusTipVoteCount ? eventVoteCount / seriesBonusTipVoteCount : 0;

  const chartColor = useMemo(() => {
    const percentageInt = Math.ceil(percentageOfVotes * 100);

    if (percentageInt > 50) {
      return GREEN;
    }

    if (percentageInt > 35) {
      return YELLOW;
    }

    return RED;
  }, [percentageOfVotes]);

  return (
    <AdvancedPieChart
      data={[
        {
          key: 'others',
          value:
            seriesEventBonusTipVotes.seriesBonusTipVoteCount -
            seriesEventBonusTipVotes.eventVoteCount,
          color: 'rgba(241,241,241, .2)',
        },
        {
          key: 'user',
          value: seriesEventBonusTipVotes.eventVoteCount ?? 0, // currentUserEntryCount,
          color: chartColor,
        },
      ]}
      label={({ dataEntry }) => (dataEntry.key === 'user' ? `${dataEntry.value}%` : '')}
      labelStyle={() => ({
        fill: chartColor,
        fontSize: '12px',
        fontWeight: 'bold',
        fontFamily: 'Rubik',
        letterSpacing: 1,
      })}
    />
  );
};

const EventVoteCard: FC<{
  eventId: string;
  seriesEventBonusTipVotes: SeriesEventBonusTipVotes;
  moveButtons: boolean;
  forceAuth: boolean;
}> = ({ eventId, seriesEventBonusTipVotes, moveButtons, forceAuth }) => {
  const { track, getIpAddress } = useAnalytics();
  const navigate = useNavigate();
  const { submitBonusTipVoteAsync } = useSubmitBonusTipVoteMutation();
  const didSubmitRef = useRef(false);
  const [didVote, setDidVote] = useState(false);
  const [didAttemptVote, setDidAttemptVote] = useState(false);
  const { openAuthGate } = useForceAuth();
  const { openEntertainerFollowUpAuthDialog } = useEntertainerFollowUpAuthDialog();
  const { referredBy } = useReferredBy();

  const { eventVoteCount, seriesBonusTipVoteCount } = useMemo(() => {
    const eventVoteCount = seriesEventBonusTipVotes.eventVoteCount + (didVote ? 1 : 0);
    const seriesBonusTipVoteCount =
      seriesEventBonusTipVotes.seriesBonusTipVoteCount + (didVote ? 1 : 0);

    return { eventVoteCount, seriesBonusTipVoteCount };
  }, [didVote, seriesEventBonusTipVotes]);

  const buttonText = useMemo(() => {
    if (didVote) {
      return 'Thanks for supporting!';
    }

    if (!seriesEventBonusTipVotes.canUserVote) {
      return 'Already supported';
    }

    if (forceAuth) {
      const options = ['Show some love ❤️', 'Support the cast 💸', 'Support for bonus tip 💸'];

      return options[Math.floor(Math.random() * options.length)];
    }

    return 'Support for bonus tip 💸';
  }, [didVote, seriesEventBonusTipVotes.canUserVote, forceAuth]);

  const handleVote = async () => {
    track('Submit review screen vote', {
      selectedPosterId: eventId,
      ipAddress: getIpAddress(),
      seriesBonusTipId: seriesEventBonusTipVotes.seriesBonusTipId,
      buttonText,
      forceAuth,
      referredBy,
    });

    setDidAttemptVote(true);

    if (!didSubmitRef.current) {
      const authCallback = async () => {
        didSubmitRef.current = true;
        await submitBonusTipVoteAsync({
          eventId,
          distinctId: mixpanel.get_distinct_id(),
          seriesBonusTipId: seriesEventBonusTipVotes.seriesBonusTipId,
          regionId: getIpAddress().split('.'),
          showEventReviewSplash: false,
          referredBy,
        });

        if (forceAuth) {
          openEntertainerFollowUpAuthDialog();
        }

        setDidVote(true);
      };

      if (forceAuth) {
        openAuthGate(authCallback);
      } else {
        await authCallback();
      }
    }
  };

  return (
    <>
      <Card>
        <div className="pt-3 pb-2 px-3">
          {USE_PROGRESS_BAR && (
            <div className="mb-2">
              <VoteMilestoneProgress eventVoteCount={eventVoteCount} />
            </div>
          )}
          <div className="d-flex align-items-center gap-3">
            {!USE_PROGRESS_BAR && (
              <div className="d-flex flex-column align-items-center">
                <div style={{ width: 80 }}>
                  <PercentageOfVotesChart
                    eventVoteCount={eventVoteCount}
                    seriesBonusTipVoteCount={seriesBonusTipVoteCount}
                    seriesEventBonusTipVotes={seriesEventBonusTipVotes}
                  />
                </div>
              </div>
            )}
            <div>
              <div>
                The cast of this show could earn a{' '}
                {formatCentsAmountToDollars(seriesEventBonusTipVotes?.seriesBonusTipAmount)} bonus
                tip! Use the button below to show support and then share the link!
              </div>
              {/* <div
            className="d-flex gap-1 align-items-center mt-2 pb-1"
            style={{ fontSize: '.7rem', whiteSpace: 'nowrap' }}
          >
            <span style={{ color: chartColor }}>
              <FontAwesomeIcon name="circle" />
            </span>
            <span>% of votes</span>
          </div> */}
            </div>
          </div>
        </div>
        <Card.Footer>
          {moveButtons ? (
            <Button
              roundness="rounded"
              color="neutral"
              variant="smooth"
              onClick={() => {
                track('Bot detection', {
                  button: 'Vote card',
                });
                navigate(`/noauth/series/denver-drag/bonus-tip-weekend`);
              }}
            >
              More info
            </Button>
          ) : (
            <Button
              roundness="rounded"
              color="neutral"
              variant="smooth"
              disabled={didVote || !seriesEventBonusTipVotes.canUserVote}
              onClick={handleVote}
            >
              {buttonText}
            </Button>
          )}
        </Card.Footer>
      </Card>

      {didAttemptVote && (
        <div className="mt-2">
          <div
            className="relative"
            style={{
              background: 'rgba(255,255,255,.1)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
            onClick={() => {
              track('Follow to IG');
              navigate(`https://instagram.com/coloradodragguide`);
            }}
          >
            <div className="d-flex gap-2 align-items-center py-1">
              <div className="flex-shrink-0">
                <div className="ps-2" style={{ width: 60, height: 60 }}>
                  <img
                    src={dollarsSvgUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      margin: '0 auto',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </div>
              <div>
                <div>
                  <strong>Be ready for next time!</strong>
                </div>
                <div style={{ opacity: 0.75, lineHeight: 1.3, fontSize: '.8rem' }}>
                  <span>
                    Tap to learn more to support casts for the next Bonus Tip. We do them often!
                  </span>
                </div>
              </div>
              <div className="ml-auto px-3">
                <FontAwesomeIcon name="chevron-right" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const EditEventPopup: FC<{ isVisible: boolean; onClose: () => void; eventId: string }> = ({
  isVisible,
  onClose,
  eventId,
}) => {
  const { event: fetchedEvent } = useEventQuery(eventId);
  const { updateEventAsync } = useUpdateEventMutation({ eventId });

  return (
    <CreateEditEventPopup
      isLoading={!fetchedEvent?._id}
      isVisible={isVisible && !!eventId}
      onClose={() => onClose()}
      event={fetchedEvent}
      cityOptions={
        fetchedEvent?._id ? [{ label: fetchedEvent?.cityId, value: fetchedEvent?.cityId }] : []
      }
      onSubmit={async (payload) => {
        await updateEventAsync(payload);
        onClose();
      }}
    />
  );
};

const UserEntertainerLikeToggle: FC<{
  eventId: string;
  entertainerId: string;
  entertainerTitle: string;
  defaultValue: boolean;
}> = ({ eventId, entertainerId, entertainerTitle, defaultValue }) => {
  const [isLiked, setIsLiked] = useState(defaultValue);
  const { track } = useAnalytics();
  const { toggleEventCompetitionLikeAsync } = useToggleEventCompetitionLikeMutation();
  const { createAuthGateHandler } = useAuthGate();
  const { openVerticalButtonsDialog } = useDialog();

  const openAuthGate = (updatedValue: boolean) => {
    createAuthGateHandler(
      async () => {
        try {
          await toggleEventCompetitionLikeAsync({
            eventId,
            entertainerId,
            isLiked: updatedValue,
          });
          setIsLiked(updatedValue);
        } catch (error) {
          setIsLiked(!updatedValue);
        }
      },
      {
        authTitle: `Send love to ${entertainerTitle}`,
        authMessage: `One heart per person, so it stays real. Enter your number and we'll text you a code.`,
        onAuthCancelled: () => {
          openVerticalButtonsDialog({
            title: `Sign in to send love`,
            text: `One heart per performer, so it stays real.`,
            buttons: [
              {
                strong: true,
                text: 'Continue',
                onClick: () => {
                  track('Dialog open sign in');
                  openAuthGate(updatedValue);
                },
              },
              {
                text: 'Not now',
                onClick: () => {
                  setTimeout(() => {
                    setIsLiked(false);
                  }, 500);
                },
              },
            ],
          });
        },
      },
    );
  };

  const handleToggleLike = () => {
    const updatedValue = !isLiked;
    setIsLiked(updatedValue);
    track('Toggle event competition like', {
      eventId,
      entertainerId,
      isLiked: updatedValue,
    });

    openAuthGate(updatedValue);
  };

  return (
    <div className="d-flex gap-2" onClick={handleToggleLike}>
      <InteractiveHeartIcon isLiked={isLiked} />
    </div>
  );
};

const FireAnimation: FC<{
  start: number;
}> = ({ start }) => {
  const animationRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (animationRef.current) {
      animationRef.current.goToAndPlay(start, true);
    }
  }, []);

  return <Lottie lottieRef={animationRef} animationData={fireAnimation} loop={true} />;
};

const USE_TRENDING_ENTERTAINERS = true;
var TRENDING_ENTERTAINERS_ICON: 'avatar' | 'instagram' = 'avatar';

const CommentButton: FC<{
  entertainer: { _id: string; unviewedCommentCount?: number };
  onClick: () => void;
}> = ({ entertainer, onClick }) => {
  const [didClick, setDidClick] = useState(false);

  const handleClick = () => {
    onClick();
    setDidClick(true);
  };

  return (
    <div onClick={handleClick} style={{ position: 'relative' }}>
      <FontAwesomeIcon name="comment-outline" />
      {!!entertainer.unviewedCommentCount && !didClick && (
        <div
          style={{
            position: 'absolute',
            zIndex: 1000,
            top: 2,
            right: -2,
            width: 8,
            height: 8,
            backgroundColor: 'red',
            borderRadius: '50%',
          }}
        ></div>
      )}
    </div>
  );
};

const TrendingEntertainersSection: FC<{
  eventId: string;
  entertainers: Record<string, Entertainer>;
  onOpenEntertainerReviewsPopup: (entertainerId: string) => void;
}> = ({ eventId, entertainers, onOpenEntertainerReviewsPopup }) => {
  const { eventCompetitionData, eventCompetitionDataIsReady } =
    useEventCompetitionDataQuery(eventId);

  const { event } = useEventQuery(eventId);
  const { isPastEvent } = useIsPastEvent(event);
  const navigate = useNavigate();

  const handleOpenEntertainerReviewsPopup = async (entertainer: { _id: string }) => {
    onOpenEntertainerReviewsPopup(entertainer._id);
  };

  const featuredEntertainers = useMemo<
    | {
        _id: string;
        title: string;
        bookingHandle?: string;
        contact?: { instagram?: string };
        avatar?: string;
        reason?: string;
        commentCount?: number;
        unviewedCommentCount?: number;
        textColor?: string;
      }[]
    | undefined
  >(() => {
    const entertainersById =
      Object.keys(entertainers).length > 0 ? entertainers : keyBy(event?.entertainers, '_id');

    if (USE_TRENDING_ENTERTAINERS && eventCompetitionData?.trendingEntertainers?.length) {
      const trendingEntertainers = eventCompetitionData?.trendingEntertainers ?? [];
      return (
        trendingEntertainers?.map(({ entertainerId, icon, reason }) => ({
          ...entertainersById[entertainerId],
          reason,
        })) ?? []
      );
    }

    const entertainerIds = isPastEvent
      ? eventCompetitionData?.top3PostcompetitionEntertainerIds
      : eventCompetitionData?.top3PrecompetitionEntertainerIds;

    return entertainerIds?.map((id) => entertainersById[id]);
  }, [eventCompetitionData, event?.entertainers, entertainers, isPastEvent]);

  const currentUserLikes = isPastEvent
    ? eventCompetitionData?.currentUser.postcompetitionLikes
    : eventCompetitionData?.currentUser.precompetitionLikes;

  if (!featuredEntertainers?.length) {
    return <></>;
  }

  const listItemIconStyle = {
    backgroundColor: '#1F1408',
    leftBorderColor: '#FF9F45',
    labelText: '#FFB566',
  };

  return (
    <>
      <TitleToolbar text="🔥 Trending before the premiere" size="sm" className="mb-0" />{' '}
      <p
        className="mt-0"
        style={{ fontSize: 12, opacity: 0.8, fontWeight: 400 }}
      >{`Most fan love leading up to the competition.`}</p>
      <List className="mt-0 mb-2">
        {featuredEntertainers?.map((entertainer, i) => (
          <ListItem
            key={`trending-${entertainer._id}-${currentUserLikes?.[entertainer._id] ?? false}`}
            style={{
              backgroundColor: listItemIconStyle?.backgroundColor ?? 'rgba(255,255,255,.1)',
              marginTop: 8,
              borderRadius: 8,
              borderLeft: listItemIconStyle?.leftBorderColor
                ? `4px solid ${listItemIconStyle.leftBorderColor}`
                : undefined,
            }}
          >
            <div
              slot="title"
              onClick={() => {
                if (entertainer.bookingHandle) {
                  navigate(`/noauth/entertainer/${entertainer.bookingHandle}`);
                }
              }}
            >
              <div className="d-flex align-items-center gap-1">
                <SeoLink href={getEntertainerSeoLink(entertainer)}>{entertainer.title}</SeoLink>
                {TRENDING_ENTERTAINERS_ICON === 'instagram' && (
                  <div style={{ width: 24, height: 24 }}>
                    <FireAnimation start={i * 5} />
                  </div>
                )}
              </div>
            </div>

            {entertainer.reason && (
              <div slot="footer" style={{ color: listItemIconStyle?.labelText }}>
                {entertainer.reason}
              </div>
            )}

            <div slot="media">
              {entertainer.contact?.instagram && TRENDING_ENTERTAINERS_ICON === 'instagram' && (
                <IconButton
                  variant="flat"
                  color="neutral"
                  style={{
                    borderRadius: 4,
                    fontSize: 18,
                    height: 22,
                    width: 22,
                  }}
                  onClick={() => {
                    openExternalUrl(`https://instagram.com/${entertainer.contact?.instagram}`);
                  }}
                >
                  <FontAwesomeIcon faStyle="fab" name="instagram" />
                </IconButton>
              )}
              {TRENDING_ENTERTAINERS_ICON === 'avatar' && (
                <div style={{ position: 'relative' }}>
                  <UserAvatar displayName={entertainer.title} avatarUrl={entertainer.avatar} />
                  <div
                    style={{ width: 24, height: 24, position: 'absolute', bottom: -4, right: -4 }}
                  >
                    <FireAnimation start={i * 5} />
                  </div>
                </div>
              )}
            </div>
            <div slot="after">
              <div className="d-flex gap-2 align-items-center">
                <CommentButton
                  entertainer={entertainer}
                  onClick={() => {
                    handleOpenEntertainerReviewsPopup(entertainer);
                  }}
                />

                <div>
                  {eventCompetitionDataIsReady && (
                    <UserEntertainerLikeToggle
                      eventId={eventId}
                      entertainerId={entertainer._id}
                      entertainerTitle={entertainer.title}
                      defaultValue={currentUserLikes?.[entertainer._id] ?? false}
                    />
                  )}
                </div>
              </div>
            </div>
          </ListItem>
        ))}
      </List>
    </>
  );
};

const EntertainersSection: FC<{
  eventId: string;
  entertainerEventReferralUrl: string | undefined;
  entertainers: Record<string, Entertainer>;
  onOpenCastReferralPopup: () => void;
  onOpenEntertainerReviewsPopup: (entertainerId: string) => void;
}> = ({
  eventId,
  onOpenCastReferralPopup,
  entertainerEventReferralUrl,
  onOpenEntertainerReviewsPopup,
  entertainers,
}) => {
  const { referredBy } = useReferredBy();
  const navigate = useNavigate();
  const { eventIsReady, event, refetchEvent } = useEventQuery(eventId);
  const { eventCompetitionData, eventCompetitionDataIsReady } =
    useEventCompetitionDataQuery(eventId);
  const { handleJoinLineup } = useJoinLineup({
    primaryEntertainerType: 'dragPerformer',
    cityId: event?.cityId,
    referredBy,
  });
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();

  const isCurrentEntertainerConfirmed = useMemo(() => {
    return currentEntertainer?._id
      ? event?.entertainers?.some(
          (entertainer) =>
            entertainer.status === 'accepted' && entertainer._id === currentEntertainer?._id,
        )
      : false;
  }, [event?.entertainers, currentEntertainer?._id]);

  const { isPastEvent } = useIsPastEvent(event);

  const currentUserLikes = isPastEvent
    ? eventCompetitionData?.currentUser.postcompetitionLikes
    : eventCompetitionData?.currentUser.precompetitionLikes;

  const filteredEntertainers = useMemo(() => {
    if (referredBy) {
      return event?.entertainers ?? [];
    }

    return (
      event?.entertainers
        ?.filter((entertainer) => entertainer.status !== 'pending')
        ?.map((entertainer) => entertainers[entertainer._id] ?? entertainer) ?? []
    );
  }, [event?.entertainers, referredBy]);

  const titleToolbar = useMemo(
    () => (
      <>
        <TitleToolbar
          text={isPastEvent ? `👏🏽 Who did the best?` : `👏🏽 Who are you rooting for?`}
          size="sm"
          className="mb-0"
        />
        <p
          className="mt-0"
          style={{ fontSize: 12, opacity: 0.8, fontWeight: 400 }}
        >{`Tap the heart. Fan love only, the judges decide the rest.`}</p>
      </>
    ),
    [],
  );

  if (!filteredEntertainers.length) {
    return <></>;
  }

  if (!eventIsReady) {
    return (
      <>
        {titleToolbar}
        <Skeleton height={100} />
      </>
    );
  }

  const handleOpenEntertainerReviewsPopup = async (entertainer: { _id: string }) => {
    onOpenEntertainerReviewsPopup(entertainer._id);
  };

  return (
    <>
      {titleToolbar}
      {filteredEntertainers.length ? (
        <>
          <List className="mt-0 mb-2" style={{ marginLeft: ' -1rem', marginRight: '-1rem' }}>
            {filteredEntertainers?.map((entertainer) => (
              <ListItem
                key={`competitor-${entertainer._id}-${currentUserLikes?.[entertainer._id] ?? false}`}
              >
                <div
                  slot="title"
                  onClick={() => {
                    if (entertainer.bookingHandle) {
                      navigate(`/noauth/entertainer/${entertainer.bookingHandle}`);
                    }
                  }}
                >
                  <SeoLink href={getEntertainerSeoLink(entertainer)}>{entertainer.title}</SeoLink>
                </div>

                <div slot="media">
                  {entertainer.contact?.instagram && (
                    <IconButton
                      variant="flat"
                      color="neutral"
                      style={{
                        borderRadius: 4,
                        fontSize: 18,
                        height: 22,
                        width: 22,
                      }}
                      onClick={() => {
                        openExternalUrl(`https://instagram.com/${entertainer.contact?.instagram}`);
                      }}
                    >
                      <FontAwesomeIcon faStyle="fab" name="instagram" />
                    </IconButton>
                  )}
                </div>
                <div slot="after">
                  <div className="d-flex gap-2 align-items-center">
                    <CommentButton
                      entertainer={entertainer}
                      onClick={() => {
                        handleOpenEntertainerReviewsPopup(entertainer);
                      }}
                    />
                    <div>
                      {eventCompetitionDataIsReady && (
                        <UserEntertainerLikeToggle
                          eventId={eventId}
                          entertainerId={entertainer._id}
                          entertainerTitle={entertainer.title}
                          defaultValue={currentUserLikes?.[entertainer._id] ?? false}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </ListItem>
            ))}
          </List>
        </>
      ) : (
        <div className="mt-2">
          <EmptyState
            title="No Cast Listed"
            text="No entertainers have been listed for this event. Are you a part of the cast? Let everyone know with the button below."
            button={{
              text: 'Add Me to This Lineup',
              onClick: () => handleJoinLineup(eventId, () => refetchEvent()),
              variant: 'smooth',
              color: 'neutral',
              roundness: 'rounded',
            }}
          />
        </div>
      )}
    </>
  );
};

export const EventCompetitionReviewsScreen = createScreenComponent<
  { eventId: string },
  { fbclid?: string }
>(({ eventId, f7route }) => {
  const { referredBy } = useReferredBy();
  const distinctId = mixpanel.get_distinct_id();

  const { track } = useAnalytics();
  const { navigate, back } = useRouter();
  const { event, entertainerEventReferralUrl } = useEventQuery(eventId);
  const { eventReviews = [], seriesEventBonusTipVotes } = useEventReviewsQuery(eventId, distinctId);
  const { currentUser } = useCurrentUser();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const { entertainersAreReady, entertainers } = useHydratedEntertainersQuery(
    event?.cityId ?? '',
    'dragPerformer',
  );

  const [isCastReferralPopupVisible, setIsCastReferralPopupVisible] = useState(false);
  const [isEditEventPopupVisible, setIsEditEventPopupVisible] = useState(false);
  const [submittedComments, setSubmittedComments] = useState<EventReview[]>([]);
  const [replyReview, setReplyReview] = useState<EventReview>();

  const [selectedReviewEntertainerId, setSelectedReviewEntertainerId] = useState<string>();
  const { entertainer: selectedReviewEntertainer } = useEntertainerByIdQuery(
    selectedReviewEntertainerId ?? '',
  );

  const [isCommentFormVisible, setIsCommentFormVisible] = useState(false);
  const handleOpenCommentForm = () => {
    setIsCommentFormVisible(true);
  };

  const handleSubmitted = (eventReview: EventReview) => {
    setSubmittedComments((prev) => [...prev, { ...eventReview, isCurrentUsersComment: true }]);
    setIsCommentFormVisible(false);
  };

  const reviews = useMemo<EventReview[]>(() => {
    return orderBy([...eventReviews, ...submittedComments], ['postedAt'], ['desc']);
  }, [submittedComments, eventReviews]);

  const reviewsGroupedByParentReviewId = useMemo(() => {
    return groupBy(
      reviews.filter((review) => review.parentReviewId),
      'parentReviewId',
    );
  }, [reviews]);

  const reviewsWithoutParentReviews = useMemo(() => {
    // Recursively finds the most recent postedAt across a review and all its descendants
    const getMostRecentPostedAt = (reviewId: string): string => {
      const children = reviewsGroupedByParentReviewId[reviewId] ?? [];
      const allDates = [
        reviews.find((r) => r._id === reviewId)?.postedAt ?? '',
        // Each child call recurses into its own children, grandchildren, etc.
        ...children.map((child) => getMostRecentPostedAt(child._id)),
      ];
      return allDates.reduce((latest, date) => (date > latest ? date : latest), '');
    };

    return orderBy(
      reviews.filter((review) => !review.parentReviewId),
      (review) => getMostRecentPostedAt(review._id),
      'desc',
    );
  }, [reviews, reviewsGroupedByParentReviewId]);

  const userHasReviewed = useMemo(() => {
    return reviews.some(({ isCurrentUsersComment }) => isCurrentUsersComment);
  }, [reviews]);

  const { isShareInstructionVisible, handleShareInstructionAcknowledged } =
    useShareInstructionIsVisible(500);

  const pastSeries = useMemo(() => {
    if (event?.startDate) {
      const eventStartDate = dayjs(event.startDate);

      return event?.series?.find(
        (series) =>
          series.lastShowFeaturePostDate &&
          eventStartDate.isSameOrBefore(dayjs(series.lastShowFeaturePostDate)),
      );
    }
    return undefined;
  }, [event?.series, event?.startDate]);

  const forceAuth = useMemo(() => {
    // return (
    //   f7route.query?.fbclid ===
    //   'PAZXh0bgNhZW0CMTEAc3J0YwZhcHBfaWQPNTY3MDY3MzQzMzUyNDI3AAGnO5m65Y-XnWoo2PR4zzaerhO7kyUB21gwWkw-vdC4ynFAuvUPh0XrF1XlLhU_aem_8KdJGsOY4YeIxL8XIKfuUw'
    // );
    return reviews?.length > 50;
  }, [f7route.query?.fbclid, currentUser?._id, reviews?.length]);

  const moveButtons = useMemo(() => {
    return false; // !currentUser?._id && reviews?.length > 50;
  }, [reviews?.length]);

  const reviewToggleButton = useMemo(() => {
    if (moveButtons) {
      const options = ['Share hype', 'Add to the hype', 'Post some hype'];

      return options[Math.floor(Math.random() * options.length)];
    }

    return currentUser?._id ? 'Hype and earn more' : 'Leave some hype';
  }, [moveButtons, currentUser?._id]);

  const onEditEvent = () => {
    setIsEditEventPopupVisible(true);
  };

  const didInitPopup = useRef(false);
  useEffect(() => {
    if (
      !didInitPopup.current &&
      !!event &&
      entertainerEventReferralUrl &&
      event?.entertainers?.filter((entertainer) => entertainer.status === 'pending')?.length
    ) {
      didInitPopup.current = true;
      setIsCastReferralPopupVisible(true);
    }
  }, [event, entertainerEventReferralUrl]);

  const handleReply = (review: EventReview) => {
    setIsCommentFormVisible(true);
    setReplyReview(review);
  };

  const { isPastEvent } = useIsPastEvent(event);
  const {
    isCurrentUserAttending,
    isCurrentUserInterested,
    isUpdatingRsvp,
    handleMarkInterested,
    handleMarkAttending,
  } = useEventRsvpActions({ eventId, eventTitle: event?.title }, referredBy);

  return (
    <Screen
      name="Event Reviews Screen"
      disableLocationPermissionGate
      headerProps={{
        title: isPastEvent ? 'Competition Thoughts' : 'Upcoming Competition',
        // right: (
        //   <InternalShareLinkButton
        //     isShareInstructionVisible={isShareInstructionVisible}
        //     onShareInstructionAcknowledged={handleShareInstructionAcknowledged}
        //     eventId={eventId}
        //     competitionLinkUrl={event?.competitionLink?.url}
        //   />
        // ),
      }}
      bottomToolbar={
        <Screen.BottomToolbar maxWidth={425}>
          {isPastEvent ? (
            <div className="w-100">
              <div className="d-flex gap-2">
                <Button
                  roundness="rounded"
                  variant="smooth"
                  color="neutral"
                  onClick={handleOpenCommentForm}
                >
                  Share your thoughts
                </Button>
              </div>
            </div>
          ) : (
            <div className="d-flex gap-2 w-100">
              <div className="w-50">
                <Button
                  noWrap
                  roundness="rounded"
                  disabled={isUpdatingRsvp}
                  variant={isCurrentUserAttending ? 'default' : 'outlined'}
                  startIcon={
                    <FontAwesomeIcon
                      faStyle="far"
                      name={isCurrentUserAttending ? 'check-circle' : 'circle'}
                    />
                  }
                  onClick={handleMarkAttending}
                >
                  Attending
                </Button>
              </div>
              <div className="w-50">
                <Button
                  noWrap
                  roundness="rounded"
                  disabled={isUpdatingRsvp}
                  color="neutral"
                  variant={isCurrentUserInterested ? 'default' : 'outlined'}
                  startIcon={
                    <FontAwesomeIcon
                      faStyle="far"
                      name={isCurrentUserInterested ? 'check-circle' : 'circle'}
                    />
                  }
                  onClick={handleMarkInterested}
                >
                  Interested
                </Button>
              </div>
            </div>
          )}
        </Screen.BottomToolbar>
      }
    >
      <Screen.Content>
        {event && (
          <>
            <ShareSection eventId={eventId} competitionLinkUrl={event?.competitionLink?.url} />

            <div
              className="d-flex align-items-center gap-3 py-2 px-3 mb-3"
              style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8 }}
              onClick={() => {
                navigate(`/noauth/series/denver-drag?tab=postersReviews&forceBack=true`, {
                  state: {
                    back: `/events/${eventId}/competition-reviews`,
                  },
                });
              }}
            >
              <img src={calendarIconUrl} height={24} width={24} />
              <div className="d-flex align-items-center justify-content-between w-100 gap-3">
                <div className="d-grid">
                  <strong>Colorado Drag Guide</strong>
                  <span
                    style={{ fontSize: '.7rem', opacity: 0.8 }}
                  >{`Tap for more local shows and performers.`}</span>
                </div>
                <FontAwesomeIcon name="chevron-right" />
              </div>
            </div>

            <img className="w-100" src={event.img} style={{ borderRadius: 8 }} />
            <EventHeader event={event} hideRsvp={true} subtitleDirection="row" />

            {!isPastEvent ? (
              <>
                <div className="mt-1">
                  <EventRsvpsSection
                    titleOverride="🗓️ RSVPs"
                    suggestedFriendsTitleOverride="🤗 Suggested friends"
                    eventId={eventId}
                    referredBy={referredBy}
                  />
                </div>

                <TrendingEntertainersSection
                  eventId={eventId}
                  entertainers={entertainers}
                  onOpenEntertainerReviewsPopup={setSelectedReviewEntertainerId}
                />

                <EntertainersSection
                  eventId={eventId}
                  entertainerEventReferralUrl={entertainerEventReferralUrl}
                  entertainers={entertainers}
                  onOpenCastReferralPopup={() => setIsCastReferralPopupVisible(true)}
                  onOpenEntertainerReviewsPopup={setSelectedReviewEntertainerId}
                />
              </>
            ) : (
              <>
                <TrendingEntertainersSection
                  eventId={eventId}
                  entertainers={entertainers}
                  onOpenEntertainerReviewsPopup={setSelectedReviewEntertainerId}
                />

                <EntertainersSection
                  eventId={eventId}
                  entertainerEventReferralUrl={entertainerEventReferralUrl}
                  entertainers={entertainers}
                  onOpenCastReferralPopup={() => setIsCastReferralPopupVisible(true)}
                  onOpenEntertainerReviewsPopup={setSelectedReviewEntertainerId}
                />

                {event?.competitionLink?.recapMessage && (
                  <>
                    <TitleToolbar text="👀 What happened?" size="sm" />
                    <Markdown options={{ forceWrapper: true, forceBlock: true }}>
                      {event.competitionLink.recapMessage}
                    </Markdown>
                  </>
                )}

                <div className="mt-4">
                  {reviewsWithoutParentReviews.length ? (
                    <div>
                      <TitleToolbar text="🔥 Audience thoughts" size="sm" />
                      <p className="mt-0">
                        Here's what folks are saying about the competition this week! Share your
                        thoughts below.
                      </p>
                      <div className="d-grid gap-2">
                        {reviewsWithoutParentReviews.map((review) => (
                          <EventCommentCard
                            key={review._id}
                            review={review}
                            reviewsGroupedByParentReviewId={reviewsGroupedByParentReviewId}
                            onReply={handleReply}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <EmptyState
                      title="Nothing shared"
                      text="No one has share any thoughts for this week. Be the first to share your thoughts!"
                      button={{
                        text: 'Share your thoughts',
                        onClick: handleOpenCommentForm,
                      }}
                    />
                  )}
                </div>
              </>
            )}
            <div style={{ height: 48 }} />
          </>
        )}
      </Screen.Content>
      <Popup isVisible={isCommentFormVisible} onClose={() => setIsCommentFormVisible(false)}>
        <Popup.Content>
          {isCommentFormVisible && (
            <FormContent
              hasFeatureBeenPosted={!!pastSeries}
              lastShowFeaturePostDate={pastSeries?.lastShowFeaturePostDate}
              eventId={eventId}
              replyReview={replyReview}
              onSubmit={handleSubmitted}
              forceAuth={forceAuth}
            />
          )}
        </Popup.Content>
        <EntertainerRewiewsPopup
          isVisible={!!selectedReviewEntertainerId}
          entertainerId={selectedReviewEntertainerId ?? ''}
          entertainerTitle={selectedReviewEntertainer?.title ?? ''}
          headerElement={
            selectedReviewEntertainer ? (
              <SupportEntertainerCard
                entertainer={selectedReviewEntertainer as any}
                cityId={event?.cityId}
                back={`/events/${eventId}/competition-reviews`}
                onNavigateCallback={() => setSelectedReviewEntertainerId(undefined)}
              />
            ) : (
              <Skeleton height={98} />
            )
          }
          onClose={() => setSelectedReviewEntertainerId(undefined)}
        />
      </Popup>
    </Screen>
  );
});
